import { Box, LinearProgress, Paper, Step, StepLabel, Stepper, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { QUERY_KEYS } from '../../utils/queryKeys';
import Logo from '../sidebar/Logo';
import StepIconComponent from './components/StepIconComponent';
import CountrySelection from './steps/CountrySelection';
import FinalStep from './steps/FinalStep';
import IntegrationStep from './steps/IntegrationStep';

const getSteps = (t: (key: string) => string) => [
    t('onboarding.steps.country'),
    t('onboarding.steps.integrations'),
    t('onboarding.steps.getStarted'),
];

const OnboardingPage = () => {
    const { t } = useTranslation();
    const steps = getSteps(t);
    const { currentUser, setIsNewUser } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [activeStep, setActiveStep] = useState(0);
    const [selectedCountry, setSelectedCountry] = useState('Germany');
    const theme = useTheme();

    useEffect(() => {
        queryClient.prefetchQuery({
            queryKey: [QUERY_KEYS.INTEGRATIONS],
        });
    }, [queryClient]);

    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    const handleNext = async () => {
        if (activeStep === steps.length - 1) {
            setIsNewUser(false);
            navigate('/plans?onboarding');
            return;
        }

        if (activeStep === 0) {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTEGRATIONS] });
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <CountrySelection
                        selectedCountry={selectedCountry}
                        onCountryChange={setSelectedCountry}
                        onNext={handleNext}
                    />
                );
            case 1:
                return <IntegrationStep onIntegrationAdded={() => {}} onNext={handleNext} onBack={handleBack} />;
            case 2:
                return <FinalStep onNext={handleNext} onBack={handleBack} />;
            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                padding: 3,
            }}
        >
            <Paper
                elevation={theme.palette.mode === 'dark' ? 0 : 3}
                sx={{
                    width: '100%',
                    maxWidth: '1100px',
                    overflow: 'hidden',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (theme) => theme.palette.divider,
                    boxShadow: (theme) => `0px 5px 15px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        minHeight: { xs: 'auto', sm: '700px' },
                    }}
                >
                    <Box
                        sx={{
                            minWidth: '350px',
                            color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.primary.contrastText,
                            display: 'flex',
                            flexDirection: 'column',
                            padding: 4,
                            borderRight: '1px solid', 
                            borderColor: (theme) => theme.palette.divider,
                        }}
                    >
                        <Box
                            sx={{
                                paddingLeft: 4,
                                marginBottom: 4,
                                textAlign: 'left',
                            }}
                        >
                            <Logo width={80} height={80} />
                        </Box>

                        <Stepper
                            activeStep={activeStep}
                            orientation="vertical"
                            sx={{
                                paddingLeft: 6,
                                '.MuiStepLabel-label': {
                                    fontSize: '1rem',
                                    fontWeight: 'normal',
                                    marginLeft: 1, 
                                },
                                '.MuiStepLabel-root': {
                                    marginBottom: 3, 
                                },
                                '.MuiStepLabel-active': {
                                    color: `${theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black} !important`,
                                    fontWeight: 'bold',
                                },
                                '.MuiStepIcon-root': {
                                    display: 'none', 
                                },
                                '.MuiStep-root': {
                                    padding: 0,
                                    position: 'relative', 
                                    marginBottom: 3, 
                                },
                                '.MuiStepConnector-line': {
                                    display: 'none', 
                                },
                                '.MuiStepConnector-root': {
                                    display: 'none', 
                                },
                                '.MuiStepLabel-iconContainer': {
                                    paddingRight: 0,
                                },
                                
                            }}
                        >
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel StepIconComponent={StepIconComponent}>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        <Box 
                            sx={{
                                flexGrow: 1,
                            }}
                        >
                            
                        </Box>
                        

                        <Box
                            sx={{
                                paddingLeft: 6,
                                paddingRight: 6,
                                marginTop: 'auto',
                                paddingTop: 4,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                            >
                                <LinearProgress
                                    variant="determinate"
                                    value={((activeStep + 1) / steps.length) * 100}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: '#FF5722',
                                        },
                                    }}
                                />
                                {/* Step progress text removed as requested */}
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            flexGrow: 1,
                            padding: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            color: 'white',
                            height: '700px', 
                            overflowY: 'auto', 
                        }}
                    >
                        <Typography
                            component="h1"
                            variant="h4"
                            gutterBottom
                            sx={{
                                fontWeight: 600,
                                marginBottom: 3,
                            }}
                        >
                            {steps[activeStep]}
                        </Typography>
                        {renderStepContent()}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default OnboardingPage;
